import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getStats, getEquityCurve, setKillSwitch, togglePaperTrading } from '../../api/client';
import useWebSocket from '../../hooks/useWebSocket';
import MarketCalendar from '../Analytics/MarketCalendar';
import FyersConnect from '../FyersConnect/FyersConnect';
import BrokerSelector from '../Brokers/BrokerSelector';
import IntradayPnlChart from './IntradayPnlChart';
import ActivityFeed from './ActivityFeed';
import RiskMeter from './RiskMeter';

const _devWs = (process.env.REACT_APP_API_URL || 'http://localhost:8010').replace(/^http/, 'ws');
const API_WS = typeof window !== 'undefined'
  ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`
  : _devWs;

export default function Dashboard() {
  const [stats, setStats]           = useState(null);
  const [equity, setEquity]         = useState([]);
  const [confirm, setConfirm]       = useState(false);
  const [paperLocal, setPaperLocal] = useState(null);
  const [paperBusy, setPaperBusy]   = useState(false);
  const { data: wsData, isConnected } = useWebSocket(`${API_WS}/api/dashboard/ws`);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (wsData) {
      setStats(prev => prev ? {
        ...prev,
        daily_pnl: wsData.daily_pnl,
        is_kill_switch_active: wsData.kill_switch,
        ...(paperBusy ? {} : { paper_trading: wsData.paper_trading }),
      } : prev);
    }
  }, [wsData, paperBusy]);

  const fetchData = async () => {
    try {
      const [s, e] = await Promise.all([getStats(), getEquityCurve()]);
      setStats(s.data);
      setPaperLocal(null);
      setEquity(e.data.equity_curve || []);
    } catch {}
  };

  const handleKillSwitch = async () => {
    if (!confirm) { setConfirm(true); return; }
    try {
      await setKillSwitch(!stats?.is_kill_switch_active);
      await fetchData();
    } catch {}
    setConfirm(false);
  };

  const handlePaperToggle = async (e) => {
    const next = e.target.checked;
    setPaperLocal(next);
    setPaperBusy(true);
    try {
      await togglePaperTrading(next);
      await fetchData();
    } catch {
      setPaperLocal(!next);
    } finally {
      setPaperBusy(false);
    }
  };

  const pnl     = stats?.daily_pnl ?? 0;
  const isPaper = paperLocal !== null ? paperLocal : (stats?.paper_trading ?? true);

  return (
    <div>
      <BrokerSelector />
      <FyersConnect />

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Dashboard</h1>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          color: isPaper ? 'var(--yellow)' : 'var(--green)',
          fontSize: 13, fontWeight: 600,
          opacity: paperBusy ? 0.6 : 1,
          cursor: paperBusy ? 'not-allowed' : 'pointer',
        }}>
          {isPaper ? '🟡 Paper Mode' : '🟢 Live Mode'}
          <label className="toggle" style={{ cursor: paperBusy ? 'not-allowed' : 'pointer' }}>
            <input type="checkbox" checked={isPaper} onChange={handlePaperToggle} disabled={paperBusy} />
            <span className="toggle-slider" />
          </label>
        </label>
      </div>

      {/* Stat cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Daily P&amp;L</div>
          <div className={`stat-value ${pnl >= 0 ? 'green' : 'red'}`}>
            ₹{pnl.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open Positions</div>
          <div className="stat-value">{stats?.open_positions ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Trades Today</div>
          <div className="stat-value">{stats?.total_trades ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Win Rate</div>
          <div className="stat-value">{stats?.win_rate ?? 0}%</div>
        </div>
      </div>

      {/* Row 1: Intraday P&L chart + Risk Meter */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <IntradayPnlChart />
        <RiskMeter
          stats={stats}
          onKillSwitch={handleKillSwitch}
          confirmKill={confirm}
          onCancelKill={() => setConfirm(false)}
        />
      </div>

      {/* Row 2: Activity Feed + Equity Curve + Market Calendar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <ActivityFeed />

        {/* Equity Curve */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Equity Curve</div>
          <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={equity}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} tickLine={false} axisLine={false} width={48}
                tickFormatter={v => `₹${Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`} />
              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}
                formatter={v => [`₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, 'Cumulative P&L']} />
              <Line type="monotone" dataKey="cumulative_pnl" stroke="var(--accent)" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
          </div>
        </div>

        <MarketCalendar />
      </div>
    </div>
  );
}
